import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  ClipboardCheck,
  ClipboardList,
  IndianRupee,
  Package,
  PackageCheck,
  RefreshCcw,
  Store,
  Truck,
  Users,
} from "lucide-react";

import {
  restaurantAdminService,
  rowsOf,
  unwrap,
} from "../../services/restaurantAdmin.service";
import operationsHeroImage from "../../assets/restaurant-operations-storefront.png";
import operationsSloganImage from "../../assets/restaurant-operations-slogan.png";

const THEME = "#d86a78";
const ORANGE = "#ef9440";
const GREEN = "#6bbf59";

const HEADING_WEIGHT = "font-bold";
const TEXT_WEIGHT = "font-semibold";
const SOFT_WEIGHT = "font-medium";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function toNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function formatCurrency(value) {
  const num = toNumber(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(value) {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getStatus(value) {
  return String(value || "").toUpperCase();
}

function getOrderAmount(order = {}) {
  return toNumber(
    order.grandTotal ??
    order.grand_total ??
    order.totalAmount ??
    order.total_amount ??
    order.netAmount ??
    order.net_amount ??
    order.amount ??
    0
  );
}

function getRestaurantName(row = {}) {
  return (
    row.restaurantName ||
    row.restaurant_name ||
    row.name ||
    row.title ||
    "Restaurant"
  );
}

function getOrderId(row = {}) {
  return (
    row.orderNo ||
    row.order_no ||
    row.orderCode ||
    row.order_code ||
    row.id ||
    row.orderId ||
    "Order"
  );
}

function getFoodRequestName(row = {}) {
  return (
    row.name ||
    row.title ||
    row.payload?.name ||
    row.payload?.title ||
    row.requestType ||
    row.request_type ||
    "Catalog request"
  );
}

function getTrendPoint(item = {}) {
  return toNumber(
    item.totalSales ??
    item.total_sales ??
    item.totalRevenue ??
    item.total_revenue ??
    item.revenue ??
    item.amount ??
    item.total ??
    0
  );
}

function MiniLineChart({ points = [], color = THEME }) {
  const safePoints = Array.isArray(points)
    ? points.map((v) => Number(v || 0)).filter((v) => Number.isFinite(v))
    : [];

  const chartPoints = safePoints.length >= 2 ? safePoints.slice(-8) : [0, 0];
  const max = Math.max(...chartPoints, 1);
  const min = Math.min(...chartPoints, 0);
  const range = Math.max(max - min, 1);

  const width = 320;
  const height = 56;
  const paddingX = 3;
  const paddingY = 7;

  const coordinates = chartPoints.map((value, index) => {
    const x =
      paddingX +
      index * ((width - paddingX * 2) / Math.max(chartPoints.length - 1, 1));

    const y =
      height -
      paddingY -
      ((value - min) / range) * (height - paddingY * 2);

    return { x, y };
  });

  const linePath = coordinates
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`)
    .join(" ");

  const areaPath = `${linePath} L${width} ${height} L0 ${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="h-[54px] w-full overflow-visible"
      aria-hidden="true"
    >
      <path d={areaPath} fill={color} opacity="0.08" />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const STAT_TONES = {
  pink: {
    color: "#f24f70",
    iconBg: "#fff0f4",
    border: "#f7dfe5",
    background: "linear-gradient(145deg, #ffffff 0%, #fffafb 60%, #fff5f7 100%)",
  },
  blue: {
    color: "#2f86f6",
    iconBg: "#edf6ff",
    border: "#dceafb",
    background: "linear-gradient(145deg, #ffffff 0%, #fbfdff 58%, #f1f8ff 100%)",
  },
  orange: {
    color: "#ff9518",
    iconBg: "#fff5e8",
    border: "#f8e7ce",
    background: "linear-gradient(145deg, #ffffff 0%, #fffdf9 58%, #fff7ec 100%)",
  },
  purple: {
    color: "#7b5cff",
    iconBg: "#f2efff",
    border: "#e7e1ff",
    background: "linear-gradient(145deg, #ffffff 0%, #fdfcff 58%, #f6f3ff 100%)",
  },
};

function StatCard({
  icon: Icon,
  value,
  label,
  delta,
  deltaTone = "positive",
  footer,
  trend = [],
  tone = "pink",
}) {
  const palette = STAT_TONES[tone] || STAT_TONES.pink;
  const deltaClass =
    deltaTone === "positive"
      ? "text-emerald-500"
      : deltaTone === "negative"
        ? "text-rose-500"
        : "text-slate-400";

  return (
    <div
      className="relative min-h-[154px] min-w-0 overflow-hidden rounded-[16px] border p-3.5 shadow-[0_8px_24px_rgba(37,56,88,0.06)]"
      style={{ borderColor: palette.border, background: palette.background }}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: palette.iconBg, color: palette.color }}
        >
          <Icon size={19} strokeWidth={2.1} />
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          <div className="truncate text-[11px] font-medium text-slate-500">
            {label}
          </div>

          <div className="mt-0.5 flex min-w-0 items-baseline gap-2.5">
            <div
              title={String(value ?? "")}
              className="truncate text-[20px] font-extrabold leading-none text-slate-950 tabular-nums"
            >
              {value}
            </div>

            {delta ? (
              <div className={cn("shrink-0 text-[10px] font-semibold", deltaClass)}>
                ↑ {delta}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mt-2 h-[54px]">
        <MiniLineChart points={trend} color={palette.color} />
      </div>

      <div className="mt-1.5 flex items-center gap-1.5 text-[9px] font-medium text-slate-400">
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: palette.color }}
        />
        <span className="truncate">{footer}</span>
      </div>
    </div>
  );
}

const ACTION_TONES = {
  pink: {
    color: "#ef4f69",
    icon: "#ef4f69",
    background: "linear-gradient(135deg, #fff7f8 0%, #fff1f4 100%)",
    border: "#f8d9df",
  },
  orange: {
    color: "#f28d18",
    icon: "#f7941d",
    background: "linear-gradient(135deg, #fffaf4 0%, #fff3e4 100%)",
    border: "#f6dfbf",
  },
  green: {
    color: "#16a77b",
    icon: "#20aa83",
    background: "linear-gradient(135deg, #f5fffb 0%, #eafaf4 100%)",
    border: "#cceee1",
  },
  blue: {
    color: "#277ff1",
    icon: "#3c8ff1",
    background: "linear-gradient(135deg, #f7fbff 0%, #edf6ff 100%)",
    border: "#d6e9fb",
  },
};

function AlertCard({ icon: Icon, count, title, action, tone = "pink", onClick }) {
  const palette = ACTION_TONES[tone] || ACTION_TONES.pink;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[88px] w-full items-center gap-3 rounded-[14px] border px-4 py-3 text-left shadow-[0_7px_18px_rgba(37,56,88,0.04)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_26px_rgba(37,56,88,0.09)]"
      style={{ borderColor: palette.border, background: palette.background }}
    >
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white shadow-sm"
        style={{ backgroundColor: palette.icon }}
      >
        <Icon size={21} strokeWidth={2} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-bold leading-none text-slate-950">
          {count}
        </div>

        <div className="mt-1.5 truncate text-[11px] font-medium text-slate-500">
          {title}
        </div>

        <div
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold"
          style={{ color: palette.color }}
        >
          <span className="truncate">{action}</span>
          <ArrowRight
            size={12}
            className="transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </div>
      </div>
    </button>
  );
}

function DonutChart({ title, centerValue, centerLabel, rows, variant = "orders" }) {
  const colors =
    variant === "orders"
      ? [ORANGE, GREEN, "#a7a7a7", THEME]
      : [THEME, ORANGE, "#a7a7a7", GREEN];

  const safeRows = Array.isArray(rows) ? rows : [];
  const values = safeRows.map((row) => Math.max(0, Number(row?.value || 0)));
  const total = values.reduce((sum, value) => sum + value, 0);

  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const gap = total > 0 ? 4 : 0;

  let runningOffset = 0;

  const segments = values.map((value, index) => {
    const rawLength = total > 0 ? (value / total) * circumference : 0;
    const length = value > 0 ? Math.max(rawLength - gap, 1) : 0;

    const segment = {
      color: colors[index] || THEME,
      dasharray: `${length} ${circumference - length}`,
      dashoffset: -runningOffset,
    };

    runningOffset += rawLength;

    return segment;
  });

  return (
    <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
      <div className={`mb-5 text-[16px] ${HEADING_WEIGHT} text-black`}>
        {title}
      </div>

      <div className="flex flex-col items-center gap-5 sm:flex-row">
        <div className="relative h-[140px] w-[140px] shrink-0">
          <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
            <circle
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke="#f3f4f6"
              strokeWidth="13"
            />

            {segments.map((seg, index) => (
              <circle
                key={index}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth="13"
                strokeLinecap="round"
                strokeDasharray={seg.dasharray}
                strokeDashoffset={seg.dashoffset}
              />
            ))}
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <div className={`text-[20px] ${TEXT_WEIGHT} text-black`}>
              {centerValue}
            </div>
            <div className="text-[10px] font-medium uppercase text-slate-400">
              {centerLabel}
            </div>
          </div>
        </div>

        <div className="w-full space-y-3">
          {safeRows.map((row, index) => (
            <div
              key={row.label}
              className="flex items-center justify-between gap-3 text-[13px]"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: colors[index] || THEME }}
                />
                <span className="truncate text-slate-500">
                  {row.label}
                </span>
              </div>

              <span className={`${TEXT_WEIGHT} text-black`}>
                {row.value}
              </span>
            </div>
          ))}

          {!safeRows.length ? (
            <div className="text-sm text-slate-400">No data yet</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function LoadingBox() {
  return (
    <div className="flex min-h-[320px] items-center justify-center rounded-[20px] border border-[#f2c6cc] bg-white text-slate-500">
      <RefreshCcw className="mr-2 animate-spin" size={18} />
      Loading food dashboard...
    </div>
  );
}

function ErrorBox({ error, onRetry }) {
  if (!error) return null;

  return (
    <div className="mb-5 flex flex-col gap-3 rounded-[16px] border border-rose-200 bg-rose-50 p-4 text-rose-700 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-[13px] font-semibold">
        <AlertTriangle size={17} />
        {String(error?.message || error)}
      </div>

      <button
        type="button"
        onClick={onRetry}
        className="rounded-[12px] border border-rose-200 bg-white px-4 py-2 text-[12px] font-semibold text-rose-700 hover:bg-rose-100"
      >
        Retry
      </button>
    </div>
  );
}

function RowStatus({ value }) {
  const raw = String(value || "-");
  const s = raw.toUpperCase();

  const ok = [
    "ACTIVE",
    "APPROVED",
    "PAID",
    "DELIVERED",
    "OPEN",
    "AUTO_ACCEPTED",
    "PLACED",
  ].includes(s);

  const warn = [
    "PENDING",
    "PAYMENT_PENDING",
    "DRIVER_ASSIGNING",
  ].includes(s);

  const cls = ok
    ? "bg-emerald-50 text-emerald-700"
    : warn
      ? "bg-amber-50 text-amber-700"
      : "bg-rose-50 text-rose-700";

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase ${cls}`}>
      {raw}
    </span>
  );
}

export default function FoodOverview() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({});
  const [restaurants, setRestaurants] = useState([]);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [
        summaryRes,
        restaurantsRes,
        requestsRes,
        ordersRes,
        salesRes,
      ] = await Promise.allSettled([
        restaurantAdminService.getRestaurantSummary({ period: "month" }),
        restaurantAdminService.listRestaurants({ limit: 8 }),
        restaurantAdminService.listFoodRequests({ status: "PENDING", limit: 8 }),
        restaurantAdminService.listFoodOrders({ limit: 8 }),
        restaurantAdminService.getRestaurantSales({ period: "month", limit: 8 }),
      ]);

      const rejected = [summaryRes, restaurantsRes, requestsRes, ordersRes].find(
        (x) => x.status === "rejected"
      );

      if (rejected) setError(rejected.reason);

      if (summaryRes.status === "fulfilled") {
        setSummary(unwrap(summaryRes.value));
      }

      if (restaurantsRes.status === "fulfilled") {
        setRestaurants(rowsOf(restaurantsRes.value));
      }

      if (requestsRes.status === "fulfilled") {
        setRequests(rowsOf(requestsRes.value));
      }

      if (ordersRes.status === "fulfilled") {
        setOrders(rowsOf(ordersRes.value));
      }

      if (salesRes.status === "fulfilled") {
        setSales(rowsOf(salesRes.value));
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const data = useMemo(() => {
    const s = summary?.summary || summary?.data?.summary || summary || {};

    const deliveredOrders = orders.filter((o) =>
      ["DELIVERED", "COMPLETED"].includes(getStatus(o.status || o.order_status))
    ).length;

    const pendingOrders = orders.filter((o) =>
      [
        "PAYMENT_PENDING",
        "PLACED",
        "AUTO_ACCEPTED",
        "DRIVER_ASSIGNING",
        "DRIVER_ASSIGNED",
        "OUT_FOR_DELIVERY",
      ].includes(getStatus(o.status || o.order_status))
    ).length;

    const activeRestaurants = restaurants.filter(
      (r) =>
        r.isActive !== false &&
        r.is_active !== false &&
        !["BLOCKED", "DELETED", "INACTIVE"].includes(getStatus(r.status))
    ).length;

    const openRestaurants = restaurants.filter(
      (r) => r.isOpen === true || r.is_open === true
    ).length;

    const pendingRequests =
      requests.filter((r) => getStatus(r.status || "PENDING") === "PENDING").length ||
      toNumber(s.pendingRequests ?? s.pending_requests);

    const orderRevenue = orders.reduce(
      (sum, order) => sum + getOrderAmount(order),
      0
    );

    const summaryRevenue = toNumber(
      s.totalRevenue ?? s.revenue ?? s.grossRevenue ?? s.gross_revenue ?? 0
    );

    const revenue = summaryRevenue || orderRevenue;

    const totalOrders =
      toNumber(s.totalOrders ?? s.orders ?? s.total_orders) || orders.length;

    const totalRestaurants =
      toNumber(s.totalRestaurants ?? s.restaurants ?? s.total_restaurants) ||
      restaurants.length;

    const totalEmployees =
      toNumber(s.totalEmployees ?? s.employees ?? s.total_employees) || 0;

    const trend = sales.map(getTrendPoint).filter(Boolean);

    return {
      revenue,
      totalOrders,
      totalRestaurants,
      totalEmployees,
      pendingRequests,
      deliveredOrders,
      pendingOrders,
      activeRestaurants,
      openRestaurants,
      trend: trend.length
        ? trend
        : [revenue * 0.45, revenue * 0.52, revenue * 0.66, revenue * 0.72, revenue],
    };
  }, [summary, restaurants, requests, orders, sales]);

  const orderRows = useMemo(
    () => [
      { label: "Live / Active", value: data.pendingOrders },
      { label: "Delivered", value: data.deliveredOrders },
      {
        label: "Recent sample",
        value: Math.max(orders.length - data.pendingOrders - data.deliveredOrders, 0),
      },
      { label: "Pending approvals", value: data.pendingRequests },
    ],
    [data, orders.length]
  );

  const restaurantRows = useMemo(
    () => [
      { label: "Active", value: data.activeRestaurants },
      { label: "Open now", value: data.openRestaurants },
      {
        label: "Closed / inactive",
        value: Math.max(restaurants.length - data.openRestaurants, 0),
      },
      { label: "Total listed", value: data.totalRestaurants },
    ],
    [data, restaurants.length]
  );

  if (loading) return <LoadingBox />;

  return (
    <div>
      <ErrorBox error={error} onRetry={load} />

      <section
        className="relative mb-3 overflow-hidden rounded-[18px] border border-[#f5d7dc] shadow-[0_7px_24px_rgba(58,72,102,0.05)]"
        style={{
          background:
            "linear-gradient(98deg, #fff8f9 0%, #fffafa 30%, #fff3f5 56%, #fff9f5 100%)",
        }}
      >
        <div className="absolute inset-y-0 left-[39%] hidden w-[35%] rounded-[45%] bg-[#ffe9ed]/65 blur-[1px] xl:block" />
        <div className="relative grid min-h-[172px] grid-cols-1 items-center gap-5 px-5 py-5 md:px-6 xl:grid-cols-[1.15fr_0.95fr_1fr] xl:gap-4 xl:px-7 xl:py-3.5">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.02em] text-[#ef5268] shadow-[0_3px_12px_rgba(239,82,104,0.08)]">
              <PackageCheck size={13} strokeWidth={2} />
              Restaurant Operations
            </div>

            <h1 className="mt-3 whitespace-nowrap text-[25px] font-extrabold leading-none tracking-[-0.035em] text-[#111827] sm:text-[30px] xl:text-[31px]">
              Restaurant <span className="text-[#ef5268]">Dashboard</span>
            </h1>

            <p className="mt-2 max-w-[460px] text-[11px] leading-[1.65] text-slate-500 xl:text-[11.5px]">
              Manage restaurants, approval requests, employees, live orders, sales and
              <br className="hidden 2xl:block" /> module settings from one place.
            </p>
          </div>

          <div className="relative hidden h-[142px] items-end justify-center xl:flex">
            <img
              src={operationsHeroImage}
              alt="Restaurant storefront"
              className="relative z-10 h-[142px] w-auto max-w-full object-contain drop-shadow-[0_8px_12px_rgba(86,64,73,0.08)]"
            />
          </div>

          <div className="flex min-w-0 flex-col items-stretch gap-3 xl:items-end">
            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 xl:max-w-[365px]">
              <button
                type="button"
                onClick={load}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-[10px] border border-[#f3d7dc] bg-white/90 px-3 text-[10px] font-semibold text-[#ef5268] shadow-sm transition hover:bg-white"
              >
                <RefreshCcw size={13} />
                Refresh
              </button>

              <button
                type="button"
                onClick={() => navigate("/admin/food-dashboard/restaurants")}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-[10px] bg-[#e75469] px-3 text-[10px] font-semibold text-white shadow-[0_7px_16px_rgba(231,84,105,0.2)] transition hover:bg-[#d94b60]"
              >
                <Store size={13} />
                Add Restaurant
              </button>

              <button
                type="button"
                onClick={() => navigate("/admin/food-dashboard/approvals")}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-[10px] border border-[#f3d7dc] bg-white/90 px-3 text-[10px] font-semibold text-[#ef5268] shadow-sm transition hover:bg-white"
              >
                <ClipboardCheck size={13} />
                Review Food
              </button>
            </div>

            <img
              src={operationsSloganImage}
              alt="Better Operations, Happier Customers"
              className="hidden h-[62px] w-auto max-w-[225px] object-contain xl:block"
            />
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={IndianRupee}
          value={formatCurrency(data.revenue)}
          label="Revenue"
          delta="+12.5%"
          footer="Current month"
          trend={data.trend}
          tone="pink"
        />

        <StatCard
          icon={ClipboardList}
          value={data.totalOrders}
          label="Orders"
          delta="+33.3%"
          footer="Auto accept flow"
          trend={[1, 2, 3, 3, 2, 4, 5, Math.max(data.totalOrders, 6)]}
          tone="blue"
        />

        <StatCard
          icon={Store}
          value={data.totalRestaurants}
          label="Restaurants"
          delta="0%"
          deltaTone="neutral"
          footer={`${data.openRestaurants} open now`}
          trend={[1, 2, 2.2, 1.5, 2, 3.2, 2.4, Math.max(data.totalRestaurants, 3.3)]}
          tone="orange"
        />

        <StatCard
          icon={Users}
          value={data.totalEmployees || "-"}
          label="Restaurant Emp"
          delta="N/A"
          deltaTone="neutral"
          footer="Auth-service employees"
          trend={[0.7, 1.3, 1.55, 1.75, 2.3, 2.3, 1.8, data.totalEmployees || 2.8]}
          tone="purple"
        />
      </div>

      <div className="mt-2.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
        <AlertCard
          icon={ClipboardCheck}
          count={data.pendingRequests}
          title="Pending approvals"
          action="Review requests"
          tone="pink"
          onClick={() => navigate("/admin/food-dashboard/approvals")}
        />

        <AlertCard
          icon={Store}
          count={data.openRestaurants}
          title="Restaurants open now"
          action="Manage restaurants"
          tone="orange"
          onClick={() => navigate("/admin/food-dashboard/restaurants")}
        />

        <AlertCard
          icon={Package}
          count="Catalog"
          title="Food Catalog"
          action="Open catalog"
          tone="green"
          onClick={() => navigate("/admin/food-dashboard/items")}
        />

        <AlertCard
          icon={Truck}
          count={data.pendingOrders}
          title="Live orders"
          action="Track orders"
          tone="blue"
          onClick={() => navigate("/admin/food-dashboard/orders")}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5 shadow-sm xl:col-span-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-[18px] font-bold text-black">
                Recent food orders
              </h2>
              <p className="text-[12px] text-slate-400">
                Latest restaurant order activity
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/admin/food-dashboard/orders")}
              className="rounded-[10px] border border-[#f2c6cc] px-3 py-2 text-[12px] font-semibold text-[#d86a78] hover:bg-[#fff6f7]"
            >
              View all
            </button>
          </div>

          <div className="mt-5 overflow-x-auto rounded-[14px] border border-[#f2c6cc]">
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead className="bg-[#fff8f9] text-[11px] uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Order</th>
                  <th className="px-4 py-3 font-medium">Restaurant</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#f2c6cc]">
                {orders.map((order, index) => (
                  <tr
                    key={order.id || order.orderId || index}
                    className="hover:bg-[#fffafb]"
                  >
                    <td className="px-4 py-3 font-semibold text-black">
                      {getOrderId(order)}
                    </td>

                    <td className="px-4 py-3 text-slate-500">
                      {getRestaurantName(order)}
                    </td>

                    <td className="px-4 py-3 font-semibold text-black">
                      {formatCurrency(getOrderAmount(order))}
                    </td>

                    <td className="px-4 py-3">
                      <RowStatus value={order.status || order.order_status || "PLACED"} />
                    </td>

                    <td className="px-4 py-3 text-slate-400">
                      {formatDate(order.createdAt || order.created_at)}
                    </td>
                  </tr>
                ))}

                {!orders.length ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-10 text-center text-slate-400"
                    >
                      No recent food orders found
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-5">
          <DonutChart
            title="Order split"
            centerValue={data.totalOrders}
            centerLabel="Orders"
            rows={orderRows}
            variant="orders"
          />

          <DonutChart
            title="Restaurant split"
            centerValue={data.totalRestaurants}
            centerLabel="Stores"
            rows={restaurantRows}
            variant="restaurants"
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-[18px] font-bold text-black">
                Pending food approvals
              </h2>
              <p className="text-[12px] text-slate-400">
                Restaurant add/edit requests waiting for super admin
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/admin/food-dashboard/approvals")}
              className="rounded-[10px] border border-[#f2c6cc] px-3 py-2 text-[12px] font-semibold text-[#d86a78] hover:bg-[#fff6f7]"
            >
              Open
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {requests.slice(0, 6).map((request, index) => (
              <button
                key={request.id || index}
                type="button"
                onClick={() => navigate("/admin/food-dashboard/approvals")}
                className="flex w-full items-center justify-between gap-4 rounded-[14px] bg-[#fffafb] p-3 text-left hover:bg-[#fff6f7]"
              >
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold text-black">
                    {getFoodRequestName(request)}
                  </div>
                  <div className="mt-1 truncate text-[12px] text-slate-500">
                    {getRestaurantName(request)}
                  </div>
                </div>

                <RowStatus value={request.status || "PENDING"} />
              </button>
            ))}

            {!requests.length ? (
              <div className="rounded-[14px] border border-dashed border-[#f2c6cc] p-8 text-center text-sm text-slate-400">
                No pending food approvals
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-[18px] font-bold text-black">
                Restaurant quick list
              </h2>
              <p className="text-[12px] text-slate-400">
                Active and recent restaurant partners
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/admin/food-dashboard/restaurants")}
              className="rounded-[10px] border border-[#f2c6cc] px-3 py-2 text-[12px] font-semibold text-[#d86a78] hover:bg-[#fff6f7]"
            >
              Manage
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {restaurants.slice(0, 6).map((restaurant, index) => (
              <button
                key={restaurant.id || index}
                type="button"
                onClick={() => navigate("/admin/food-dashboard/restaurants")}
                className="flex w-full items-center justify-between gap-4 rounded-[14px] bg-[#fffafb] p-3 text-left hover:bg-[#fff6f7]"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fff1f3] font-semibold text-[#d86a78]">
                    {String(restaurant.name || "R").charAt(0).toUpperCase()}
                  </div>

                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-semibold text-black">
                      {restaurant.name || "Restaurant"}
                    </div>

                    <div className="mt-1 truncate text-[12px] text-slate-500">
                      {restaurant.city || restaurant.address?.city || restaurant.email || "-"}
                    </div>
                  </div>
                </div>

                <RowStatus
                  value={
                    restaurant.isOpen || restaurant.is_open
                      ? "OPEN"
                      : restaurant.status || "ACTIVE"
                  }
                />
              </button>
            ))}

            {!restaurants.length ? (
              <div className="rounded-[14px] border border-dashed border-[#f2c6cc] p-8 text-center text-sm text-slate-400">
                No restaurants found
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}